"use client";

import { useCallback, useEffect, useState } from "react";

// A small per-device setting kept in localStorage (camera options and the
// like), so it survives closing the screen and reloading. Read after mount so
// the server render and first client render agree.
export function useDevicePreference<T>(
  key: string,
  fallback: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(fallback);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored !== null) setValue(JSON.parse(stored) as T);
    } catch {
      // Storage blocked or a malformed value: keep the fallback.
    }
  }, [key]);

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === "function" ? (next as (prev: T) => T)(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // Storage blocked: the setting still applies for this session.
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, update];
}
